/**
 * One item row (spec M1-B3 §8.1, measured on `[ui:library/table-01-top.png]`).
 *
 *   36 px tall, 1 px `#d0d4e4` bottom rule
 *   x 391-396   6 px group colour bar (`div.pulse-left-indicator`)
 *   x 397-429  33 px checkbox gutter, 14 px box
 *   x 430-1023 594 px name cell with the hover affordances at its right edge
 *   x 1024-1088 65 px conversation cell
 *   the rest scrolls horizontally
 *
 * The three-dots button sits at x 363-387, i.e. **outside** the grid, and appears on hover.
 */
import type { ItemId } from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { columnWidth } from '../columns/registry';
import { memo, type MouseEvent as ReactMouseEvent } from 'react';
import type { CellRenderContext } from '../columns/types';
import type { BoardColumn, ItemRow } from '../data/types';
import { GridCell } from './GridCell';
import { CHECKBOX_GUTTER, CONVERSATION_WIDTH, GROUP_BAR_WIDTH } from './layout';
import { isNameCellOpenGesture } from './name-cell-click';
import { ROW_DRAG_TYPE, type DropEdge } from './row-drag';
import type { CellAddress, GridSelection } from './selection';
import { isInRange, sameCell } from './selection';

export interface GridRowProps {
  item: ItemRow;
  rowIndex: number;
  groupColor: string;
  groupTitle: string;
  top: number;
  height: number;
  pinned: readonly BoardColumn[];
  scrolling: readonly BoardColumn[];
  layout: readonly { id: string; left: number; width: number }[];
  columnRange: { start: number; end: number };
  nameWidth: number;
  stickyWidth: number;
  scrollWidth: number;
  context: CellRenderContext;
  selection: GridSelection;
  editing: { address: CellAddress; initialInput?: string } | null;
  readOnlyColumnIds: readonly string[];
  /** 6i / q455: the columns whose cells wrap instead of ellipsing. */
  wrappedColumnIds?: readonly string[];
  checked: boolean;
  /**
   * `false` while a sort is active: the position a drag would write is not the order the
   * grid shows, so monday's own rule — and B4 §F6 — is to refuse the drag and say why.
   */
  rowDragEnabled: boolean;
  /** Paint the 2 px drop line above or below this row (`--pulse-drag-line-left: 38px`). */
  dropEdge?: DropEdge;
  /** M3.S1: the nested table is open for this row. */
  subitemsExpanded?: boolean;
  onToggleSubitems?(): void;
  onToggleChecked(event: ReactMouseEvent): void;
  onFocusCell(address: CellAddress, event: ReactMouseEvent): void;
  onStartEditing(address: CellAddress): void;
  onCommit(address: CellAddress, value: unknown): void;
  onCancelEditing(): void;
  onNavigate(direction: 'up' | 'down' | 'left' | 'right' | 'next' | 'prev'): void;
  onOpenMenu(x: number, y: number, context?: boolean): void;
  onOpenItem(): void;
  /**
   * **P-266.** The conversation bubble: the item card, on its Updates tab. Separate from
   * `onOpenItem` because the tab is part of what the gesture means — a reader who clicks the
   * bubble is asking for the conversation, not for whichever tab the card was last left on.
   */
  onOpenUpdates(): void;
  /**
   * **P-266.** The `+` beside the name is painted only when this is `true`: the board has a
   * `subtasks` column (monday paints no `+` at all without one) **and** the actor may write
   * items on it. A control that cannot do what it depicts is the defect the card is about.
   */
  canAddSubitem?: boolean;
  /** `+` → a subitem, created **in place**: the nested table opens under this row. */
  onAddSubitem?(): void;
  /**
   * **P-103.** What this row's drag carries: this id alone, or — when the row is part of the
   * current selection — every selected id in board order. The grid owns the answer (it has
   * the row list and the selection); the row asks for it at `dragstart`, so the callback can
   * stay identity-stable and the `memo` above keeps holding.
   */
  onDragPayload?(itemId: ItemId): string;
}

export const GridRow = memo(function GridRow({
  item,
  rowIndex,
  groupColor,
  groupTitle,
  top,
  height,
  pinned,
  scrolling,
  layout,
  columnRange,
  nameWidth,
  stickyWidth,
  scrollWidth,
  context,
  selection,
  editing,
  readOnlyColumnIds,
  wrappedColumnIds,
  checked,
  rowDragEnabled,
  dropEdge,
  subitemsExpanded = false,
  onToggleSubitems,
  onToggleChecked,
  onFocusCell,
  onStartEditing,
  onCommit,
  onCancelEditing,
  onNavigate,
  onOpenMenu,
  onOpenItem,
  onOpenUpdates,
  canAddSubitem = false,
  onAddSubitem,
  onDragPayload,
}: GridRowProps) {
  const readOnly = new Set(readOnlyColumnIds);
  const wrapped = new Set(wrappedColumnIds ?? []);
  /** P-003: the row of an open editor is tinted across its whole width, name cell included. */
  const rowEditing = editing !== null && editing.address.itemId === item.id;
  const extraPinned = pinned.filter((column) => column.id !== 'name');
  const nameColumn = pinned.find((column) => column.id === 'name');

  const cellProps = (column: BoardColumn, colIndex: number) => ({
    column,
    item,
    context,
    colIndex,
    focused: sameCell(selection.focus, { itemId: item.id, columnId: column.id }),
    inRange: isInRange(selection, { itemId: item.id, columnId: column.id }),
    editing: editing !== null && editing.address.itemId === item.id && editing.address.columnId === column.id,
    readOnly: readOnly.has(column.id),
    wrapped: wrapped.has(column.id),
    initialInput: editing?.initialInput,
    onFocusCell,
    onStartEditing,
    onCommit,
    onCancel: onCancelEditing,
    onNavigate,
  });

  return (
    <div
      className="ez-row"
      role="row"
      aria-rowindex={rowIndex + 1}
      aria-selected={checked || undefined}
      data-testid={`item-${item.id}`}
      data-editing={rowEditing || undefined}
      // Read by the canvas-level drag delegation in `Grid`.
      data-drop-row="item"
      data-item-id={item.id}
      data-drop-edge={dropEdge ?? undefined}
      style={{ top, height, width: stickyWidth + scrollWidth }}
      onContextMenu={(event) => {
        event.preventDefault();
        onOpenMenu(event.clientX, event.clientY, true);
      }}
    >
      <div className="ez-row__sticky" role="presentation" style={{ width: stickyWidth }}>
        <span
          className="ez-row__bar"
          style={{ width: GROUP_BAR_WIDTH, backgroundColor: groupColor }}
          aria-hidden="true"
        />
        {/*
         * The menu gutter and the checkbox share one `gridcell`: the three-dots button sits
         * **outside** the grid at x 363-387 `[B3§8.1]`, but in the accessibility tree it
         * belongs to the row's first cell rather than floating free inside a `row`.
         */}
        <span className="ez-row__gutter" role="gridcell" aria-colindex={1} style={{ width: CHECKBOX_GUTTER }}>
          <button
            type="button"
            className="ez-row__menu"
            data-testid={`row-menu-${item.id}`}
            aria-label={`More options for ${item.name}`}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              onOpenMenu(box.right, box.bottom);
            }}
          >
            <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
          </button>
          <input
            type="checkbox"
            className="ez-row__checkbox"
            data-testid={`row-select-${item.id}`}
            checked={checked}
            aria-label={`Select ${item.name}`}
            onChange={() => undefined}
            onClick={onToggleChecked}
          />
        </span>

        {nameColumn ? (
          <div
            className="ez-row__name"
            role="presentation"
            /**
             * M3.S1: with a count bubble the cell shrinks to its content so the bubble follows
             * the **name**, which is where monday paints it (`S1 capture parent  3`,
             * `[uis:01-row-collapsed]`). Without the flag the cell keeps filling the width, so
             * every board that has no subitems lays out exactly as it did before.
             */
            data-has-subitems={item.subitemIds.length > 0 || undefined}
            style={{ width: nameWidth }}
            /*
             * monday's drag affordance is the whole name cell (`canDrag--zcZRZ`), not a
             * separate handle and not the whole row — so a drag started over a status cell
             * is a range selection, exactly as it is there `[B3§8.1]`.
             */
            draggable={rowDragEnabled && !rowEditing}
            title={rowDragEnabled ? undefined : 'Clear the sort to drag rows'}
            data-testid={`row-drag-${item.id}`}
            /*
             * P-106: the roving focus lives on the grid's scroller, but the keyboard reorder
             * needs a node to start from — `Space` on the focused name cell. `-1` keeps it
             * out of the tab order, where monday's is not either.
             */
            tabIndex={-1}
            /**
             * **P-320.** A click beside the name text opens the card on Updates — monday's own
             * answer, measured in `discovery/ui-reference/board-view/name-cell/` and explained in
             * `name-cell-click.ts`. The capture handler keeps the cell underneath from taking
             * focus (monday leaves no selection behind) without calling `preventDefault`, so the
             * row still drags from the same pixels; a drag ends in no `click`, so it opens nothing.
             */
            onMouseDownCapture={(event) => {
              if (isNameCellOpenGesture(event)) event.stopPropagation();
            }}
            onClick={(event) => {
              if (!isNameCellOpenGesture(event)) return;
              event.stopPropagation();
              onOpenUpdates();
            }}
            onDragStart={(event) => {
              event.dataTransfer.setData(
                ROW_DRAG_TYPE,
                onDragPayload ? onDragPayload(item.id) : String(item.id),
              );
              event.dataTransfer.effectAllowed = 'move';
            }}
          >
            {/*
              * M3.S1 (`[uis:01-row-collapsed]`, `[uis:02-row-expanded]`): the chevron is
              * painted ~14 px into the name cell and exists **only** on a row that has
              * subitems — a row without one gets neither the chevron nor the count bubble.
              * The count follows the item name as a plain number.
              *
              * P-312: **or** on a row whose nested table is open — after its last subitem is
              * deleted the table stays open (monday keeps it, with "+ Add subitem"), and the
              * chevron is the only way to close it.
              */}
            {item.subitemIds.length > 0 || subitemsExpanded ? (
              /*
               * A11y (`table.spec.ts` A2, axe `aria-required-children`): the wrapper is
               * `display: contents`, so the chevron keeps its measured box, but in the
               * accessibility tree the button is inside a `gridcell` instead of hanging
               * directly off the `row` — a `row` may own only cells.
               */
              <span className="ez-row__subitems-cell" role="gridcell" aria-colindex={2}>
                <button
                  type="button"
                  className="ez-row__subitems-toggle"
                  aria-expanded={subitemsExpanded}
                  aria-label={
                    subitemsExpanded ? `Collapse subitems of ${item.name}` : `Expand subitems of ${item.name}`
                  }
                  title={subitemsExpanded ? 'Collapse subitems' : 'Expand subitems'}
                  data-testid={`subitems-toggle-${item.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleSubitems?.();
                  }}
                >
                  <Icon
                    icon={subitemsExpanded ? icons.chevronDown : icons.chevronRight}
                    size={14}
                    ignoreFocusStyle
                  />
                </button>
              </span>
            ) : null}
            <GridCell {...cellProps(nameColumn, 1)} width={nameWidth} />
            {item.subitemIds.length > 0 ? (
              <>
                {/*
                  * The count is a **text node** — round 10b briefly made it generated content
                  * from `data-count` so that `.ez-row__name`'s own `textContent` would stay the
                  * item's name, and that was the wrong trade: `verify-subitems-local.mjs` reads
                  * this bubble's text as the count (U2), and U7's block height is measured
                  * against it, so an empty bubble reported a 72 px phantom gap that was never
                  * there. monday's grid body is a `<canvas>` (`discovery/ui-reference/subitems/`
                  * INDEX §"The grid body is a canvas"), so it has no DOM here to copy either
                  * way — and a number a reader can select, copy and hear is the better DOM.
                  * `verify-actions-local.mjs` now reads a row's name from `.ez-row__name
                  * .ez-cell`, which is what `verify-board-table-outcomes-local.mjs` has always
                  * read and which is the name and nothing but the name.
                  */}
                <span
                  className="ez-row__subitems-count"
                  data-testid={`subitems-count-${item.id}`}
                  title={item.subitemIds.length === 1 ? '1 subitem' : `${item.subitemIds.length} subitems`}
                >
                  {item.subitemIds.length}
                </span>
                {/*
                  * The dead space right of the count badge. On a row with subitems the cell
                  * shrinks to its content, so **this** is the empty area beside the name — and
                  * P-320's rule owns it: a click here opens the card on Updates, exactly as it
                  * does on a row without subitems. It used to forward a `mousedown` to the cell
                  * to focus it, which was round 10b's reading of the same pixels; the sandbox
                  * capture says monday opens the card there instead, so the forwarding is gone
                  * rather than fighting the rule above it.
                  */}
                <span className="ez-row__name-filler" aria-hidden="true" />
              </>
            ) : null}
            <span className="ez-row__open-cell" role="gridcell" aria-colindex={3}>
              {/*
                * **P-266.** monday's `+` beside the name adds a subitem, and on a board with no
                * `subtasks` column it is not painted at all — so `canAddSubitem` is the *whole*
                * condition (the board has the column **and** this actor may write items). It was
                * an unlabelled `svg` with no handler, which is why the card calls it dead.
                */}
              {canAddSubitem ? (
                <button
                  type="button"
                  className="ez-row__add-subitem"
                  aria-label={`Add subitem to ${item.name}`}
                  title="Add subitem"
                  data-testid={`row-add-subitem-${item.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddSubitem?.();
                  }}
                >
                  <Icon icon={icons.add} size={14} ignoreFocusStyle />
                </button>
              ) : null}
              <button
                type="button"
                className="ez-row__open"
                aria-label={`Open ${item.name}`}
                title="Open item"
                onClick={onOpenItem}
              >
                <Icon icon={icons.expandRight} size={14} ignoreFocusStyle />
              </button>
            </span>
          </div>
        ) : null}

        <span
          className="ez-row__chat"
          role="gridcell"
          aria-colindex={4}
          style={{ width: CONVERSATION_WIDTH }}
          title={groupTitle}
        >
          {/*
            * **P-266.** The conversation bubble opens the item card on its **Updates** tab —
            * for every role that can read the item, a guest included: reading and commenting is
            * exactly what a guest is subscribed to a `share` board for, and the card's composer
            * already answers `updates.create` 200 for them. It used to be a bare `<span>` with
            * an icon: no name, no handler, no focus. monday's own name cell carries a
            * `clickable` here, labelled "Start conversation" when the item has no update yet
            * (`discovery/ui-reference/editors/name/inline-editor.json` →
            * `monday-name-cell-conversation-wrapper`); with updates on it the card asks for
            * "Updates · N".
            */}
          <button
            type="button"
            className="ez-row__chat-button"
            aria-label={
              item.updatesCount > 0 ? `Updates · ${item.updatesCount}` : 'Start conversation'
            }
            title={item.updatesCount > 0 ? 'Updates' : 'Start conversation'}
            data-testid={`row-chat-${item.id}`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenUpdates();
            }}
          >
            {item.updatesCount > 0 ? (
              <span className="ez-row__chat-count">
                <Icon icon={icons.update} size={14} ignoreFocusStyle />
                {item.updatesCount}
              </span>
            ) : (
              <Icon icon={icons.update} size={14} ignoreFocusStyle />
            )}
          </button>
        </span>

        {extraPinned.map((column, index) => (
          <GridCell key={column.id} {...cellProps(column, index + 4)} width={columnWidth(column)} />
        ))}
      </div>

      <div className="ez-row__scroll" role="presentation" style={{ width: scrollWidth }}>
        {scrolling.slice(columnRange.start, columnRange.end).map((column, offset) => {
          const index = columnRange.start + offset;
          const box = layout[index];
          if (!box) return null;
          return (
            <GridCell
              key={column.id}
              {...cellProps(column, pinned.length + index + 3)}
              left={box.left}
              width={box.width}
            />
          );
        })}
      </div>
    </div>
  );
});
